import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const HomePage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");

  useEffect(() => {
    if (token) {
      navigate("/dashboard");
    }
  }, [token, navigate]);

  return (
    <div className="min-h-screen bg-[#0B1C2D] flex flex-col font-sans selection:bg-[#E63946] selection:text-white overflow-hidden relative">
      {/* 1. BACKGROUND ANIMATION ELEMENTS */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        {/* Moving Data Grid */}
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: `linear-gradient(#E63946 1px, transparent 1px), linear-gradient(90deg, #E63946 1px, transparent 1px)`,
            backgroundSize: "50px 50px",
          }}
        ></div>
        {/* Glowing Orbs */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.2, 0.1],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-[600px] bg-[#E63946] rounded-full blur-[140px]"
        />
      </div>

      {/* 2. TOP BRAND LOGO */}
      <motion.nav
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-20 w-full px-12 py-10 flex justify-center lg:justify-start"
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center size-10 rounded-xl bg-[#E63946] text-white shadow-2xl shadow-[#E63946]/40">
            <span className="material-symbols-outlined text-2xl">hub</span>
          </div>
          <span className="text-xl font-black tracking-[0.3em] text-white uppercase">
            ContactHQ
          </span>
        </div>
      </motion.nav>

      {/* 3. HERO CONTENT */}
      <main className="flex-1 flex flex-col items-center justify-center relative z-10 px-6 text-center">
        <div className="max-w-4xl space-y-10">
          {/* Status Chip */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-md"
          >
            <span className="size-2 rounded-full bg-green-500 animate-pulse shadow-[0_0_10px_rgba(34,197,94,0.8)]"></span>
            <span className="text-white text-[9px] font-black uppercase tracking-[0.3em]">
              Master Infrastructure v2.0 Live
            </span>
          </motion.div>

          {/* Main Title */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 1 }}
            className="text-6xl md:text-[120px] font-black text-white tracking-tighter leading-[0.85] uppercase"
          >
            Command <br />
            <span
              className="text-transparent"
              style={{ WebkitTextStroke: "1px rgba(255,255,255,0.2)" }}
            >
              Center.
            </span>
          </motion.h1>

          {/* Subtext */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 1 }}
            className="max-w-xl mx-auto text-slate-400 text-sm md:text-lg font-medium leading-relaxed tracking-wide"
          >
            The unified portal for distributed node management, regional
            scaling, and secure infrastructure oversight.
          </motion.p>

          {/* ✅ THE SINGLE BUTTON */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="pt-10"
          >
            <button
              onClick={() => navigate("/login")}
              className="relative group overflow-hidden px-16 py-6 bg-[#E63946] text-white font-black text-xs uppercase tracking-[0.4em] rounded-full transition-all duration-500 hover:scale-105 hover:shadow-[0_0_50px_rgba(230,57,70,0.4)]"
            >
              {/* Button "Scan" Animation */}
              <div className="absolute inset-0 w-1/4 h-full bg-white/20 skew-x-[45deg] -translate-x-full group-hover:translate-x-[400%] transition-transform duration-1000 ease-in-out"></div>

              <span className="relative z-10 flex items-center gap-4">
                Initialize Access
                <span className="material-symbols-outlined text-sm animate-bounce-x">
                  arrow_forward
                </span>
              </span>
            </button>
          </motion.div>
        </div>
      </main>

      {/* 4. MINIMAL FOOTER */}
      <motion.footer
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.4 }}
        transition={{ delay: 1.2 }}
        className="w-full p-12 flex flex-col md:flex-row justify-between items-center gap-6"
      >
        <div className="flex gap-8">
          {["KOL", "BLR", "DEL", "RAJ"].map((node) => (
            <div
              key={node}
              className="text-[9px] font-black text-white tracking-widest"
            >
              {node} NODE
            </div>
          ))}
        </div>
        <div className="text-[9px] font-black text-white tracking-[0.4em] uppercase">
          © 2026 Distributed HQ
        </div>
      </motion.footer>

      <style jsx>{`
        @keyframes bounce-x {
          0%,
          100% {
            transform: translateX(0);
          }
          50% {
            transform: translateX(5px);
          }
        }
        .animate-bounce-x {
          animation: bounce-x 1.5s infinite;
        }
      `}</style>
    </div>
  );
};

export default HomePage;
